import React, { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { graphql } from 'react-relay';

import Button from '@atlaskit/button/default/button';
import AiGenerativeTextIcon from '@atlaskit/icon-lab/core/ai-generative-text';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import Link from '@atlaskit/link/link';

import { useLazyQueryPromiseInterop } from '@confluence/apollo-relay-interop/entry-points/useLazyQueryPromiseInterop.ts';
import { withFlags, type WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { createStagedSmartCreateDraft } from './utils/createStagedSmartCreateDraft.ts';
import { useOpenModalWithTemplate } from './hooks/useOpenModalWithTemplate.ts';
import type {
	CreateWithRovoTemplateButtonTemplatesQuery$data,
	CreateWithRovoTemplateButtonTemplatesQuery$variables,
} from './__generated__/CreateWithRovoTemplateButtonTemplatesQuery.graphql.ts';

export const i18n = defineMessages({
	createWithRovoButton: {
		id: 'smart-create.create-with-rovo-template-button',
		defaultMessage: 'Create with Rovo',
		description: 'Text for the button to create from a template with Rovo',
	},
	errorTitle: {
		id: 'smart-create.create-with-rovo-template-button.error-title',
		defaultMessage: 'Something went wrong',
		description:
			'Title for the error flag shown when creating a page with Rovo from a template fails',
	},
	errorDescription: {
		id: 'smart-create.create-with-rovo-template-button.error-description',
		defaultMessage: '<link>Refresh</link> this page and try again.',
		description:
			'Description for the error flag shown when creating a page with Rovo from a template fails. "Refresh" is a clickable link.',
	},
});

const CreateWithRovoTemplateButtonTemplatesQuery = graphql`
	query CreateWithRovoTemplateButtonTemplatesQuery($spaceKey: String!, $ids: [String!]!) {
		templateBodies(spaceKey: $spaceKey, ids: $ids) {
			nodes {
				id
				body {
					value
					representation
				}
			}
		}
	}
`;

/**
 * Session-level cache for template ADF bodies keyed by `${spaceKey}:${templateId}`.
 * Avoids re-calling `useLazyQueryPromiseInterop` on subsequent clicks — Apollo cache hits
 * don't change the `data` ref, so the hook's useEffect never re-fires and the promise hangs.
 */
export const templateBodiesCache = new Map<string, string>();

type Template = {
	contentBlueprintId?: string | null;
	templateId?: string | null;
	itemModuleCompleteKey?: string | null;
	name?: string | null;
};

type CreateWithRovoTemplateButtonProps = WithFlagsProps & {
	template: Template;
	spaceKey: string;
	onModalOpen?: () => void;
	entryPoint: string;
};

const getTemplateIdForQuery = (template: Template) => {
	return (template.contentBlueprintId || template.templateId || template.itemModuleCompleteKey)!;
};

const CreateWithRovoTemplateButtonInner = ({
	template,
	spaceKey,
	onModalOpen,
	entryPoint,
	flags,
}: CreateWithRovoTemplateButtonProps) => {
	const intl = useIntl();
	const { cloudId } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const fetchTemplateBodies = useLazyQueryPromiseInterop<
		CreateWithRovoTemplateButtonTemplatesQuery$data,
		CreateWithRovoTemplateButtonTemplatesQuery$variables
	>(CreateWithRovoTemplateButtonTemplatesQuery);
	const openModalWithTemplate = useOpenModalWithTemplate({
		assistanceServiceParams: {
			product: 'confluence',
			experienceId: 'ai-mate',
			touchpointSource: 'template-gallery-smart-create',
			siteId: cloudId ?? '',
		},
	});

	const fetchTemplateAdf = useCallback(async (): Promise<string | undefined> => {
		const templateId = getTemplateIdForQuery(template);
		const cacheKey = `${spaceKey}:${templateId}`;

		const cachedTemplateBody = templateBodiesCache.get(cacheKey);
		if (cachedTemplateBody !== undefined) {
			return cachedTemplateBody;
		}

		const templateBodiesData = await fetchTemplateBodies({
			variables: { spaceKey, ids: [templateId] },
		});
		const templateNode = templateBodiesData?.templateBodies?.nodes?.find(
			(node) => node?.id === templateId,
		);

		const { value: adfBody, representation } = templateNode?.body ?? {};

		if (!adfBody || representation !== 'atlas_doc_format') {
			return undefined;
		}

		templateBodiesCache.set(cacheKey, adfBody);
		return adfBody;
	}, [fetchTemplateBodies, spaceKey, template]);

	const handleFailure = useCallback(
		(reason: string, errorMessage?: string) => {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'failed',
					actionSubject: 'createWithRovoTemplateButton',
					source: entryPoint === 'gallery' ? 'templateGallery' : 'templateRightRail',
					attributes: {
						templateId: template.templateId,
						spaceKey,
						entryPoint,
						reason,
						...(errorMessage && { errorMessage }),
					},
				},
			}).fire();

			void flags.showErrorFlag({
				title: intl.formatMessage(i18n.errorTitle),
				description: intl.formatMessage(i18n.errorDescription, {
					link: (chunks: React.ReactNode) => (
						<Link href={window.location.href} onClick={() => window.location.reload()}>
							{chunks}
						</Link>
					),
				}),
			});

			setIsLoading(false);
		},
		[createAnalyticsEvent, entryPoint, template.templateId, spaceKey, flags, intl],
	);

	const handleClick = useCallback(async () => {
		setIsLoading(true);

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'createWithRovoTemplateButton',
				source: entryPoint === 'gallery' ? 'templateGallery' : 'templateRightRail',
				attributes: {
					templateId: template.templateId,
					spaceKey,
					entryPoint,
				},
			},
		}).fire();

		try {
			if (!cloudId) {
				handleFailure('missingCloudId');
				return;
			}

			const stagedResponse = await createStagedSmartCreateDraft(cloudId);
			const contentId = stagedResponse.contentId;

			if (!contentId) {
				handleFailure('missingContentId');
				return;
			}

			const adfBody = await fetchTemplateAdf();

			if (!adfBody) {
				handleFailure('missingTemplateAdf');
				return;
			}

			await openModalWithTemplate({
				contentId,
				adfBody,
				templateName: template.name || '',
				spaceKey,
				onOpenCallback: () => {
					onModalOpen?.();
					setIsLoading(false);
				},
			});
		} catch (error) {
			handleFailure('unexpectedError', error instanceof Error ? error.message : undefined);
		}
	}, [
		cloudId,
		template,
		spaceKey,
		fetchTemplateAdf,
		openModalWithTemplate,
		onModalOpen,
		handleFailure,
		entryPoint,
		createAnalyticsEvent,
	]);

	return (
		<Button
			appearance="default"
			iconBefore={(iconProps) => (
				<AiGenerativeTextIcon {...iconProps} color={token('color.icon')} />
			)}
			onClick={handleClick}
			isLoading={isLoading}
			testId="create-with-rovo-button"
		>
			{intl.formatMessage(i18n.createWithRovoButton)}
		</Button>
	);
};

export const CreateWithRovoTemplateButton = withFlags(CreateWithRovoTemplateButtonInner);
